import { useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { searchCatalog } from '../services/catalog';
import type { SearchResult } from '../types/database';

const labels: Record<SearchResult['type'], string> = {
  artist: '아티스트', setlist: '선곡표', venue: '공연장', festival: '페스티벌',
};
const resultOrder: SearchResult['type'][] = ['artist', 'setlist', 'venue', 'festival'];

export function SearchBox({ compact = false }: { compact?: boolean }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const resultsId = useId();

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 1) {
      setResults([]);
      setOpen(false);
      setFailed(false);
      setLoading(false);
      setActiveIndex(-1);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setFailed(false);
      searchCatalog(trimmed)
        .then((items) => {
          if (!controller.signal.aborted) {
            setResults(items);
            setActiveIndex(-1);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setResults([]);
            setFailed(true);
          }
        })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 250);
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [query]);

  useEffect(() => {
    if (open && activeIndex >= 0) document.getElementById(`${resultsId}-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open, resultsId]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const choose = (result: SearchResult) => {
    setQuery('');
    setOpen(false);
    setLoading(false);
    navigate(result.href);
  };

  return (
    <div className={`search-box ${compact ? 'search-compact' : ''}`} ref={wrapperRef} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
      <span className="search-icon" aria-hidden="true">⌕</span>
      <input
        type="search"
        value={query}
        onChange={(event) => { const value = event.target.value; setQuery(value); setActiveIndex(-1); setResults([]); setFailed(false); setLoading(Boolean(value.trim())); setOpen(Boolean(value.trim())); }}
        onFocus={() => query.trim().length >= 1 && setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
          if (event.key === 'ArrowDown' && results.length) { event.preventDefault(); setOpen(true); setActiveIndex((index) => (index + 1) % results.length); }
          if (event.key === 'ArrowUp' && results.length) { event.preventDefault(); setOpen(true); setActiveIndex((index) => index <= 0 ? results.length - 1 : index - 1); }
          if (event.key === 'Enter' && open && !loading && results.length && !failed && (activeIndex >= 0 || results.length === 1)) { event.preventDefault(); choose(results[activeIndex >= 0 ? activeIndex : 0]); }
        }}
        placeholder="아티스트, 공연, 공연장, 페스티벌 검색"
        aria-label="통합 검색"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? resultsId : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? `${resultsId}-${activeIndex}` : undefined}
        autoComplete="off"
      />
      {loading && <span className="mini-loader" aria-label="검색 중" />}
      {open && (
        <div className="search-results" id={resultsId} role="listbox" aria-label="통합 검색 결과" aria-busy={loading}>
          {loading ? <div className="search-empty" role="status">기록을 찾고 있어요…</div> : failed ? (
            <div className="search-empty" role="alert"><strong>검색을 불러오지 못했어요.</strong><span>잠시 후 다시 입력해주세요.</span></div>
          ) : results.length === 0 ? (
            <div className="search-empty"><strong>일치하는 기록이 없어요.</strong><span>검색어를 줄이거나 새 공연을 기록해보세요.</span><div><Link to="/setlists" onClick={() => setOpen(false)}>전체 선곡표</Link><Link to="/setlists/new" onClick={() => setOpen(false)}>새 기록 등록</Link></div></div>
          ) : resultOrder.map((type) => {
            const items = results.filter((result) => result.type === type);
            return items.length ? <div className="search-result-group" key={type} role="group" aria-label={labels[type]}>
              <div className="search-result-heading">{labels[type]}</div>
              {items.map((result) => { const index = results.findIndex((candidate) => candidate.type === result.type && candidate.id === result.id); return <button id={`${resultsId}-${index}`} key={`${result.type}-${result.id}`} className={index === activeIndex ? 'is-active' : ''} aria-selected={index === activeIndex} onMouseEnter={() => setActiveIndex(index)} onClick={() => choose(result)} role="option">
                <span className={`result-type type-${result.type}`}>{labels[result.type]}</span>
                <span className="result-copy"><strong>{result.title}</strong><small>{result.meta}</small></span>
                <span aria-hidden="true">→</span>
              </button>; })}
            </div> : null;
          })}
        </div>
      )}
    </div>
  );
}
